import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import MoonbucksLogo from "../components/MoonbucksLogo";
import { PLANS, startPlanCheckout } from "../components/PlanCta";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export function Login() {
  const { login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
  };

  return (
    <AuthShell title="Welcome back" sub="Log in to catch your next move.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
        </Field>
        <Field label="Password">
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
        </Field>
        <div className="text-right -mt-2">
          <Link to="/forgot-password" className="text-xs text-neutral-500 hover:text-mb-emerald" data-testid="login-forgot-link">
            Forgot password?
          </Link>
        </div>
        <Button type="submit" className="w-full bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md h-11" disabled={loading} data-testid="login-submit-btn">
          {loading ? "Signing in…" : "Log in"}
        </Button>
        <p className="text-xs text-center text-neutral-500">
          New here? <Link to="/register" className="text-mb-emerald font-semibold">Get Moonbucks</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { register, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const plan = PLANS[params.get("plan")];
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [paying, setPaying] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await register(name, email, password);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
      return;
    }
    setPaying(true);
    try {
      await startPlanCheckout(plan.id);
    } catch (err) {
      toast.error("Account created, but checkout didn't open — pick your plan on your account page.");
      nav("/app/account");
    }
  };

  if (!plan) {
    return (
      <AuthShell title="Get Moonbucks" sub="Pick your plan. Cancel anytime.">
        <PlanChooser />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Get Moonbucks" sub="Skip the latte. Catch the move.">
      <SelectedPlan plan={plan} />
      <form onSubmit={submit} className="space-y-4 mt-6" data-testid="register-form">
        <Field label="Full name">
          <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input" />
        </Field>
        <Field label="Email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email-input" />
        </Field>
        <Field label="Password">
          <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password-input" />
        </Field>
        <Button type="submit" className="w-full bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md h-11" disabled={loading || paying} data-testid="register-submit-btn">
          {loading ? "Creating…" : paying ? "Opening secure checkout…" : "Continue to payment"}
        </Button>
        <p className="text-xs text-center text-neutral-500">
          Already on Moonbucks? <Link to="/login" className="text-mb-emerald font-semibold">Log in</Link>
        </p>
      </form>
    </AuthShell>
  );
}

function SelectedPlan({ plan }) {
  return (
    <div className="rounded-md border-2 border-mb-emerald bg-mb-emerald/5 p-4" data-testid="register-selected-plan">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="label-uppercase text-mb-emerald" data-testid="register-plan-name">{plan.name}</div>
          <div className="mt-1 font-mono-data text-2xl font-extrabold text-mb-ink">{plan.price}<span className="text-sm font-normal text-neutral-500">/{plan.per}</span></div>
        </div>
        <Link to="/register" className="text-xs font-semibold text-mb-emerald hover:underline" data-testid="register-change-plan-link">Change plan</Link>
      </div>
      <div className="mt-1 text-xs text-neutral-600">Cancel anytime.</div>
      <div className="mt-2 text-xs text-neutral-700" data-testid="register-charge-line">{plan.charge}</div>
    </div>
  );
}

function PlanChooser() {
  return (
    <div className="grid gap-3" data-testid="register-plan-chooser">
      {Object.values(PLANS).map((p) => (
        <Link
          key={p.id}
          to={`/register?plan=${p.id}`}
          className={`block rounded-md border-2 p-4 transition-colors hover:border-mb-emerald ${p.id === "monthly" ? "border-mb-emerald bg-mb-emerald/5" : "border-mb-border"}`}
          data-testid={`register-choose-${p.id}`}
        >
          <div className="flex items-baseline justify-between">
            <span className="label-uppercase text-mb-emerald">{p.name}{p.id === "monthly" ? " · most popular" : ""}</span>
            <span className="font-mono-data text-xl font-extrabold text-mb-ink">{p.price}<span className="text-xs font-normal text-neutral-500">/{p.per}</span></span>
          </div>
          <div className="mt-1 text-xs text-neutral-600">{p.id === "monthly" ? "Save ~25% vs paying weekly. Cancel anytime." : "Try it for a week. Cancel anytime."}</div>
        </Link>
      ))}
      <p className="text-xs text-center text-neutral-500 mt-1">
        Already on Moonbucks? <Link to="/login" className="text-mb-emerald font-semibold">Log in</Link>
      </p>
    </div>
  );
}

function AuthShell({ title, sub, children }) {
  return (
    <div className="min-h-screen bg-mb-sand flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="auth-logo-link">
          <div className="w-10 h-10 rounded-full bg-mb-emerald flex items-center justify-center text-white">
            <MoonbucksLogo className="w-5 h-5" />
          </div>
          <span className="font-serif-hero text-3xl leading-none">Moonbucks</span>
        </Link>
        <div className="bg-white border border-mb-border rounded-lg p-8 shadow-sm">
          <h1 className="font-display font-semibold text-3xl tracking-tight">{title}</h1>
          <p className="mt-1 text-sm text-neutral-600">{sub}</p>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-uppercase text-neutral-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
